import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import {
  Button,
  Heading,
  HStack,
  Tag,
  Text,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useRef } from "react";
import { CherryPayApi } from "../../api/models";
import { RoleRbacForm } from "../../forms/RoleRbacForm";

interface RoleRbacModalProps {
  existingRole?: CherryPayApi.CherryHubRoleSummaryItem | null;
  availablePermissions: CherryPayApi.PermissionSummary[];
  product: CherryPayApi.LicencedProduct;
  onCreateRole?: (role: CherryPayApi.CherryHubRoleUpdate) => void;
  onUpdateRole?: (
    roleId: string,
    role: CherryPayApi.CherryHubRoleUpdate
  ) => void;
  isOpen: boolean;
  onClose: () => void;
}

const GrantedPermissions = ({
  permissions,
  availablePermissions,
}: {
  permissions: string[];
  availablePermissions: CherryPayApi.PermissionSummary[];
}) => {
  const permissionsByName = availablePermissions.reduce<{
    [key: string]: CherryPayApi.PermissionSummary;
  }>((obj, item) => {
    obj[item.Id!] = item;
    return obj;
  }, {});

  return (
    <Wrap w="100%" as="ul">
      {permissions.map((permission) => (
        <Tag
          key={permission}
          as="li"
          title={permissionsByName[permission]?.Description ?? ""}
        >
          {permissionsByName[permission]?.DisplayName ?? permission}
        </Tag>
      ))}
    </Wrap>
  );
};

const RoleView = ({
  role,
  onClose,
  availablePermissions,
}: {
  role: CherryPayApi.CherryHubRoleSummaryItem;
  onClose: () => void;
  availablePermissions: CherryPayApi.PermissionSummary[];
}) => {
  return (
    <VStack spacing="2" w="100%">
      <Heading as="h3" fontSize="md" w="100%">
        Name
      </Heading>
      <Text w="100%">{role.DisplayName}</Text>
      <Heading as="h3" fontSize="md" w="100%">
        Description
      </Heading>
      <Text w="100%">{role.Description || "-"}</Text>
      <Heading as="h3" fontSize="md" w="100%">
        Granted Permissions
      </Heading>
      <GrantedPermissions
        permissions={role.Permissions ?? []}
        availablePermissions={availablePermissions}
      />
      <HStack width="100%" justifyContent="end" spacing="3" pt="8">
        <Button onClick={onClose}>Close</Button>
      </HStack>
    </VStack>
  );
};

export const RoleRbacModal = ({
  existingRole,
  isOpen,
  onClose,
  availablePermissions,
  product,
  onCreateRole,
  onUpdateRole,
}: RoleRbacModalProps) => {
  const initialRef = useRef<HTMLInputElement>(null);

  const onSaveRole = useCallback(
    (role: CherryPayApi.CherryHubRoleUpdate) => {
      if (onUpdateRole && existingRole?.Meta?.Id) {
        onUpdateRole(existingRole.Meta.Id, role);
      } else if (onCreateRole) {
        onCreateRole(role);
      }
      onClose();
    },
    [existingRole, onUpdateRole, onCreateRole, onClose]
  );

  const modalTitle = useMemo(() => {
    if (!existingRole) return "New Role";
    return existingRole.IsSystemRole ? "View Role" : "Edit Role";
  }, [existingRole]);

  return (
    <Modal
      initialFocusRef={initialRef}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalBody pb="6">
          {existingRole?.IsSystemRole ? (
            <RoleView
              role={existingRole}
              availablePermissions={availablePermissions}
              onClose={onClose}
            />
          ) : (
            <RoleRbacForm
              existingRole={existingRole}
              product={product}
              onSave={onSaveRole}
              onCancel={onClose}
              availablePermissions={availablePermissions}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
